import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { PageSEO } from '../components/PageSEO';
import { toast } from 'sonner';
import {
  Compass,
  MessageSquare,
  Briefcase,
  Calculator,
  BarChart3,
  ArrowRight,
  CheckCircle2,
  Shield,
  Sparkles,
  Radar,
  FileText,
  Users,
  Lightbulb,
  Loader2,
  Send,
  DollarSign,
  Gift,
  X,
  PiggyBank,
  Crown,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const features = [
  {
    icon: MessageSquare,
    title: 'Asesor IA Laboral',
    description: 'Describe tu situación y recibe orientación basada en la Ley Federal del Trabajo. Sin tecnicismos. Sin rodeos.',
  },
  {
    icon: Briefcase,
    title: 'Evaluador de Oferta de Trabajo',
    description: 'Antes de firmar, sabe si la oferta que tienes es buena, regular o mala. Compara salario, prestaciones y condiciones en segundos.',
  },
  {
    icon: Calculator,
    title: 'Calculadora de Liquidación',
    description: 'Ingresa tus datos y obtén un estimado de cuánto te deben pagar si te despiden. Incluye indemnización, aguinaldo proporcional, vacaciones y prima vacacional.',
  },
  {
    icon: BarChart3,
    title: 'Benchmark Salarial',
    description: 'Compara tu sueldo con el mercado real en México según tu puesto e industria. Sin adivinar.',
  },
  {
    icon: DollarSign,
    title: 'Calculadora de Sueldo Neto',
    description: 'Descubre cuánto deberías recibir después de impuestos, seguridad social y descuentos.',
  },
  {
    icon: PiggyBank,
    title: 'Planeación de Retiro',
    description: '¿Sabes cuánto vas a recibir de pensión? Revisa tus semanas cotizadas, tu ley aplicable y qué tan cerca estás de tu retiro.',
    pro: true,
  },
];

const benefits = [
  'Evaluar ofertas laborales de forma objetiva',
  'Comparar tu salario con el mercado mexicano',
  'Calcular liquidación y finiquito estimados',
  'Recibir orientación laboral con IA',
  'Guardar y revisar tus resultados',
  'Planear tu retiro y pensión',
];

const comingSoonFeatures = [
  {
    id: 'radar_carrera',
    name: 'Radar de Carrera',
    icon: Radar,
    description: 'Descubre qué habilidades y movimientos pueden aumentar tu valor profesional.',
  },
  {
    id: 'optimizador_cv',
    name: 'Optimizador de CV y LinkedIn',
    icon: FileText,
    description: 'Optimiza tu CV y tu perfil de LinkedIn para mejores oportunidades.',
  },
  {
    id: 'bolsa_trabajo',
    name: 'Bolsa de Trabajo',
    icon: Users,
    description: 'Encuentra oportunidades laborales relevantes para tu perfil y experiencia.',
  },
];

const featureRoutes = {
  'Asesor IA Laboral': '/advisor',
  'Evaluador de Oferta de Trabajo': '/job-evaluator',
  'Calculadora de Liquidación': '/severance',
  'Benchmark Salarial': '/salary-benchmark',
  'Calculadora de Sueldo Neto': '/net-salary',
  'Planeación de Retiro': '/retiro',
};

export default function LandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated, loading } = useAuth();
  const [registeredInterests, setRegisteredInterests] = useState([]);
  const [interestLoading, setInterestLoading] = useState({});
  const [ideaText, setIdeaText] = useState('');
  const [submittingIdea, setSubmittingIdea] = useState(false);
  const [ideaSubmitted, setIdeaSubmitted] = useState(false);
  const [stickyDismissed, setStickyDismissed] = useState(false);

  const handleCTA = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      navigate('/register');
    }
  };

  const handleInterest = async (featureName) => {
    if (!isAuthenticated) {
      toast.info('Crea una cuenta para registrar tu interés');
      navigate('/register');
      return;
    }

    setInterestLoading((prev) => ({ ...prev, [featureName]: true }));

    try {
      const token = localStorage.getItem('klarvi_token');
      const response = await fetch(`${API_URL}/api/features/interest`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ feature_name: featureName }),
      });

      if (response.ok) {
        setRegisteredInterests((prev) => [...prev, featureName]);
        toast.success('¡Interés registrado! Te notificaremos cuando esté disponible.');
      } else if (response.status === 401) {
        toast.info('Crea una cuenta para registrar tu interés');
        navigate('/register');
      }
    } catch (error) {
      toast.error('Error al registrar interés');
    } finally {
      setInterestLoading((prev) => ({ ...prev, [featureName]: false }));
    }
  };

  const handleSubmitIdea = async () => {
    if (!ideaText.trim()) return;

    if (!isAuthenticated) {
      toast.info('Crea una cuenta para enviar tu idea');
      navigate('/register');
      return;
    }

    setSubmittingIdea(true);

    try {
      const token = localStorage.getItem('klarvi_token');
      const response = await fetch(`${API_URL}/api/features/idea`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ idea_text: ideaText }),
      });

      if (response.ok) {
        setIdeaSubmitted(true);
        setIdeaText('');
        toast.success('¡Gracias por tu idea! La revisaremos.');
      } else if (response.status === 401) {
        toast.info('Crea una cuenta para enviar tu idea');
        navigate('/register');
      }
    } catch (error) {
      toast.error('Error al enviar tu idea');
    } finally {
      setSubmittingIdea(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <PageSEO
        title="Klarvi | Herramientas laborales y retiro en México"
        description="Calcula liquidación, sueldo neto y retiro. Compara salarios, evalúa ofertas y resuelve dudas laborales. Herramientas para empleados en México."
        canonical="https://klarvi.org/"
      />
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-lg border-b">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-2.5">
              <Compass className="w-9 h-9 text-[#2563EB]" />
              <span className="font-bold text-2xl tracking-tight font-['Manrope']">
                Klarvi
              </span>
            </div>
            <div className="flex items-center gap-3">
              {!loading && !isAuthenticated && (
                <>
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/login')}
                    data-testid="login-btn"
                  >
                    Iniciar Sesión
                  </Button>
                  <Button
                    onClick={() => navigate('/register')}
                    className="bg-[#0F172A] hover:bg-[#0F172A]/90"
                    data-testid="register-btn"
                  >
                    Crear Cuenta
                  </Button>
                </>
              )}
              {!loading && isAuthenticated && (
                <Button
                  onClick={() => navigate('/dashboard')}
                  className="bg-[#0F172A] hover:bg-[#0F172A]/90"
                  data-testid="dashboard-btn"
                >
                  Ir al Dashboard
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 md:py-24 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <h1 className="text-3xl md:text-4xl lg:text-[2.75rem] font-bold tracking-tight text-[#0F172A] font-['Manrope'] leading-tight">
                Klarvi, la plataforma laboral para empleados en México
              </h1>
              <p className="mt-6 text-lg md:text-xl text-muted-foreground leading-relaxed">
                Resuelve dudas laborales, calcula finiquitos y liquidaciones, compara salarios y evalúa ofertas de trabajo.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  onClick={handleCTA}
                  className="bg-[#2563EB] hover:bg-[#2563EB]/90 h-12 px-8 text-base"
                  data-testid="hero-cta-btn"
                >
                  {isAuthenticated ? 'Ir al Dashboard' : 'Empieza gratis'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
              <div className="mt-8 flex items-center gap-2 text-sm text-muted-foreground">
                <Shield className="w-4 h-4 text-[#10B981]" />
                <span>Tu información está protegida y es confidencial</span>
              </div>
              <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
                <Shield className="w-4 h-4 text-[#10B981]" />
                <span>Basado en la Ley Federal del Trabajo de México.</span>
              </div>
            </div>
            <div className="hidden lg:flex flex-col gap-4" data-testid="hero-cards-grid">
              <div className="grid grid-cols-3 gap-4">
                <Link to="/severance" className="bg-gradient-to-br from-white to-blue-50 border-2 border-blue-100 rounded-xl p-5 shadow-md flex flex-col items-center text-center gap-3 hover:shadow-xl hover:-translate-y-1 hover:border-[#2563EB]/40 transition-all duration-200" data-testid="hero-card-severance">
                  <div className="w-14 h-14 rounded-xl bg-[#2563EB] flex items-center justify-center shadow-sm">
                    <Calculator className="w-7 h-7 text-white" />
                  </div>
                  <p className="text-sm font-semibold text-[#0F172A] leading-snug">¿Te despidieron?<br />Calcula tu liquidación</p>
                </Link>
                <Link to="/job-evaluator" className="bg-gradient-to-br from-white to-emerald-50 border-2 border-emerald-100 rounded-xl p-5 shadow-md flex flex-col items-center text-center gap-3 hover:shadow-xl hover:-translate-y-1 hover:border-emerald-400/40 transition-all duration-200" data-testid="hero-card-evaluator">
                  <div className="w-14 h-14 rounded-xl bg-emerald-500 flex items-center justify-center shadow-sm">
                    <Briefcase className="w-7 h-7 text-white" />
                  </div>
                  <p className="text-sm font-semibold text-[#0F172A] leading-snug">¿Recibiste una oferta?<br />Evalúala antes de firmar</p>
                </Link>
                <Link to="/salary-benchmark" className="bg-gradient-to-br from-white to-purple-50 border-2 border-purple-100 rounded-xl p-5 shadow-md flex flex-col items-center text-center gap-3 hover:shadow-xl hover:-translate-y-1 hover:border-purple-400/40 transition-all duration-200" data-testid="hero-card-benchmark">
                  <div className="w-14 h-14 rounded-xl bg-purple-500 flex items-center justify-center shadow-sm">
                    <BarChart3 className="w-7 h-7 text-white" />
                  </div>
                  <p className="text-sm font-semibold text-[#0F172A] leading-snug">¿Tu salario es justo?<br />Compáralo con el mercado</p>
                </Link>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <Link to="/advisor" className="bg-gradient-to-br from-white to-sky-50 border-2 border-sky-100 rounded-xl p-5 shadow-md flex flex-col items-center text-center gap-3 hover:shadow-xl hover:-translate-y-1 hover:border-sky-400/40 transition-all duration-200" data-testid="hero-card-advisor">
                  <div className="w-14 h-14 rounded-xl bg-sky-500 flex items-center justify-center shadow-sm">
                    <MessageSquare className="w-7 h-7 text-white" />
                  </div>
                  <p className="text-sm font-semibold text-[#0F172A] leading-snug">¿Tienes una duda laboral?<br />Pregunta al asesor</p>
                </Link>
                <Link to="/net-salary" className="bg-gradient-to-br from-white to-teal-50 border-2 border-teal-100 rounded-xl p-5 shadow-md flex flex-col items-center text-center gap-3 hover:shadow-xl hover:-translate-y-1 hover:border-teal-400/40 transition-all duration-200" data-testid="hero-card-net-salary">
                  <div className="w-14 h-14 rounded-xl bg-teal-500 flex items-center justify-center shadow-sm">
                    <DollarSign className="w-7 h-7 text-white" />
                  </div>
                  <p className="text-sm font-semibold text-[#0F172A] leading-snug">¿Cuánto te llega neto?<br />Calcula tu sueldo real</p>
                </Link>
                <Link to="/retiro" className="bg-gradient-to-br from-white to-indigo-50 border-2 border-indigo-100 rounded-xl p-5 shadow-md flex flex-col items-center text-center gap-3 hover:shadow-xl hover:-translate-y-1 hover:border-indigo-400/40 transition-all duration-200" data-testid="hero-card-retirement">
                  <div className="w-14 h-14 rounded-xl bg-indigo-500 flex items-center justify-center shadow-sm">
                    <PiggyBank className="w-7 h-7 text-white" />
                  </div>
                  <p className="text-sm font-semibold text-[#0F172A] leading-snug">¿Sabes cuánto vas a recibir?<br />Planea tu retiro</p>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-20 px-4 md:px-8 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] font-['Manrope']">
              ¿Qué puedes hacer con Klarvi?
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="flex items-start gap-3 p-4 rounded-lg bg-white border shadow-sm animate-slide-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <CheckCircle2 className="w-5 h-5 text-[#10B981] flex-shrink-0 mt-0.5" />
                <span className="text-[#0F172A]">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] font-['Manrope']">
              Herramientas para tu carrera
            </h2>
            <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
              Cuando te despiden, recibes una oferta o simplemente quieres saber si tu sueldo es justo, no siempre tienes a alguien que te explique con claridad. Klarvi está ahí en ese momento.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-6 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              const route = featureRoutes[feature.title];
              return (
                <Link
                  key={index}
                  to={route || '#'}
                  className="block"
                  data-testid={`feature-card-${index}`}
                >
                  <Card
                    className="relative border bg-white hover:shadow-lg transition-all duration-200 hover:-translate-y-1 animate-slide-up h-full"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    {feature.pro && (
                      <Badge
                        className="absolute top-3 right-3 bg-[#2563EB] text-white"
                        data-testid={`feature-card-${index}-pro-badge`}
                      >
                        <Crown className="w-3 h-3 mr-1" />
                        Pro
                      </Badge>
                    )}
                    <CardContent className="p-6">
                      <div className="w-12 h-12 rounded-lg bg-[#2563EB]/10 flex items-center justify-center mb-4">
                        <Icon className="w-6 h-6 text-[#2563EB]" />
                      </div>
                      <h3 className="font-semibold text-lg text-[#0F172A] font-['Manrope']">
                        {feature.title}
                      </h3>
                      <p className="mt-2 text-sm text-muted-foreground">
                        {feature.description}
                      </p>
                      <div className="mt-3 flex items-center text-xs font-medium text-[#2563EB]">
                        Ir a la herramienta <ArrowRight className="w-3 h-3 ml-1" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Coming Soon / Próximamente Section */}
      <section className="py-16 md:py-24 px-4 md:px-8 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] font-['Manrope']">
                Próximamente
              </h2>
              <Badge variant="secondary" className="text-sm px-3 py-1">
                <Sparkles className="w-3 h-3 mr-1" />
                En desarrollo
              </Badge>
            </div>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Estamos trabajando en nuevas funcionalidades. ¡Tu opinión nos ayuda a priorizar!
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Card 1: Radar de Carrera */}
            {(() => {
              const feature = comingSoonFeatures[0];
              const Icon = feature.icon;
              const isInterested = registeredInterests.includes(feature.id);
              const isLoading = interestLoading[feature.id];
              return (
                <Card
                  key={feature.id}
                  className="border bg-white/80 hover:shadow-lg transition-all duration-200 hover:-translate-y-1 animate-slide-up"
                  style={{ animationDelay: '0ms' }}
                  data-testid={`proximamente-card-${feature.id}`}
                >
                  <CardContent className="p-6 flex flex-col h-full">
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
                        <Icon className="w-6 h-6 text-slate-500" />
                      </div>
                      <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                        Próximamente
                      </Badge>
                    </div>
                    <h3 className="font-semibold text-lg text-[#0F172A] font-['Manrope']">{feature.name}</h3>
                    <p className="mt-2 text-sm text-muted-foreground flex-1">{feature.description}</p>
                    {isAuthenticated ? (
                      <Button variant={isInterested ? 'secondary' : 'outline'} size="sm" className="w-full mt-4" onClick={() => handleInterest(feature.id)} disabled={isInterested || isLoading} data-testid={`landing-interest-${feature.id}`}>
                        {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : isInterested ? <CheckCircle2 className="w-4 h-4 mr-2" /> : null}
                        {isInterested ? 'Interés registrado' : 'Me interesa'}
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" className="w-full mt-4" onClick={() => navigate('/register')} data-testid={`landing-interest-login-${feature.id}`}>Regístrate para votar</Button>
                    )}
                  </CardContent>
                </Card>
              );
            })()}

            {/* Card 2: Tu idea (campo de texto libre) */}
            <Card
              className="border bg-white/80 hover:shadow-lg transition-all duration-200 hover:-translate-y-1 animate-slide-up"
              style={{ animationDelay: '100ms' }}
              data-testid="proximamente-card-tu-idea"
            >
              <CardContent className="p-6 flex flex-col h-full">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-lg bg-[#2563EB]/10 flex items-center justify-center">
                    <Lightbulb className="w-6 h-6 text-[#2563EB]" />
                  </div>
                  <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                    Sugerencia
                  </Badge>
                </div>
                <h3 className="font-semibold text-lg text-[#0F172A] font-['Manrope']">Tu idea</h3>
                <p className="mt-2 text-sm text-muted-foreground">¿Tienes otra idea? Cuéntanos qué herramienta te gustaría ver.</p>
                {isAuthenticated ? (
                  ideaSubmitted ? (
                    <div className="mt-4 flex items-center gap-2 text-sm text-[#10B981] font-medium">
                      <CheckCircle2 className="w-4 h-4" />
                      ¡Idea enviada! Gracias.
                    </div>
                  ) : (
                    <div className="mt-4 space-y-2">
                      <Textarea placeholder="Describe tu idea..." value={ideaText} onChange={(e) => setIdeaText(e.target.value)} className="resize-none text-sm min-h-[72px]" maxLength={500} data-testid="tu-idea-textarea" />
                      <Button size="sm" className="w-full bg-[#2563EB] hover:bg-[#2563EB]/90" onClick={handleSubmitIdea} disabled={!ideaText.trim() || submittingIdea} data-testid="tu-idea-submit-btn">
                        {submittingIdea ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                        Enviar idea
                      </Button>
                    </div>
                  )
                ) : (
                  <Button variant="outline" size="sm" className="w-full mt-4" onClick={() => navigate('/register')} data-testid="landing-interest-login-tu-idea">Regístrate para sugerir</Button>
                )}
              </CardContent>
            </Card>

            {/* Card 3 & 4: Optimizador CV y Bolsa de Trabajo */}
            {comingSoonFeatures.slice(1).map((feature, index) => {
              const Icon = feature.icon;
              const isInterested = registeredInterests.includes(feature.id);
              const isLoading = interestLoading[feature.id];
              return (
                <Card
                  key={feature.id}
                  className="border bg-white/80 hover:shadow-lg transition-all duration-200 hover:-translate-y-1 animate-slide-up"
                  style={{ animationDelay: `${(index + 2) * 100}ms` }}
                  data-testid={`proximamente-card-${feature.id}`}
                >
                  <CardContent className="p-6 flex flex-col h-full">
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
                        <Icon className="w-6 h-6 text-slate-500" />
                      </div>
                      <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                        Próximamente
                      </Badge>
                    </div>
                    <h3 className="font-semibold text-lg text-[#0F172A] font-['Manrope']">{feature.name}</h3>
                    <p className="mt-2 text-sm text-muted-foreground flex-1">{feature.description}</p>
                    {isAuthenticated ? (
                      <Button variant={isInterested ? 'secondary' : 'outline'} size="sm" className="w-full mt-4" onClick={() => handleInterest(feature.id)} disabled={isInterested || isLoading} data-testid={`landing-interest-${feature.id}`}>
                        {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : isInterested ? <CheckCircle2 className="w-4 h-4 mr-2" /> : null}
                        {isInterested ? 'Interés registrado' : 'Me interesa'}
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" className="w-full mt-4" onClick={() => navigate('/register')} data-testid={`landing-interest-login-${feature.id}`}>Regístrate para votar</Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Referral Block */}
      <section className="py-12 md:py-16 px-4 md:px-8" data-testid="landing-referral-block">
        <div className="max-w-3xl mx-auto">
          <Card className="border-0 bg-gradient-to-r from-teal-600 to-teal-700 shadow-xl overflow-hidden">
            <CardContent className="p-8 md:p-10 flex flex-col md:flex-row items-center gap-6">
              <div className="w-16 h-16 rounded-2xl bg-white/15 flex items-center justify-center flex-shrink-0">
                <Gift className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-xl md:text-2xl font-bold text-white font-['Manrope']">
                  Invita a un amigo a Klarvi
                </h3>
                <p className="mt-2 text-teal-100 text-sm md:text-base leading-relaxed">
                  Si se hace Pro, tú ganas: un mes gratis o descuento en tu mensualidad. Los descuentos se acumulan hasta que tu Pro quede gratis.
                </p>
              </div>
              <Button
                size="lg"
                onClick={() => navigate(isAuthenticated ? '/dashboard' : '/login')}
                className="bg-white text-teal-700 hover:bg-white/90 font-semibold flex-shrink-0"
                data-testid="landing-referral-cta"
              >
                {isAuthenticated ? 'Ir al Dashboard' : 'Inicia sesión'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 px-4 md:px-8 bg-[#0F172A]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white font-['Manrope']">
            Tu trabajo vale lo que sabes exigir.
          </h2>
          <p className="mt-4 text-lg text-slate-300">
            Regístrate gratis y empieza a usar Klarvi hoy.
          </p>
          <Button
            size="lg"
            onClick={handleCTA}
            className="mt-8 bg-[#2563EB] hover:bg-[#2563EB]/90 h-12 px-8 text-base"
            data-testid="cta-btn"
          >
            {isAuthenticated ? 'Ir al Dashboard' : 'Empieza gratis'}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 md:px-8 border-t">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col items-center gap-5">
            <div className="flex items-center gap-2">
              <Compass className="w-5 h-5 text-[#2563EB]" />
              <span className="font-semibold font-['Manrope']">Klarvi</span>
            </div>
            <p className="text-xs text-muted-foreground text-center max-w-2xl">
              La información proporcionada es solo para fines informativos y educativos,
              y no constituye asesoramiento legal, representación legal, asesoramiento fiscal
              ni asesoramiento financiero.
            </p>
            <div className="flex items-center gap-5">
              <a href="https://www.instagram.com/klarvi520" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-[#0F172A] transition-colors" data-testid="footer-instagram" aria-label="Instagram">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              </a>
              <a href="https://www.facebook.com/people/Klarvi/61591731278892/" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-[#0F172A] transition-colors" data-testid="footer-facebook" aria-label="Facebook">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <a href="https://www.tiktok.com/@klarvi5" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-[#0F172A] transition-colors" data-testid="footer-tiktok" aria-label="TikTok">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
              </a>
              <a href="mailto:contacto@klarvi.org" className="text-muted-foreground hover:text-[#0F172A] transition-colors" data-testid="footer-email" aria-label="Correo">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
              </a>
            </div>
            <div className="flex items-center gap-3">
              <a href="/privacidad" className="text-xs text-[#2563EB] hover:underline" data-testid="landing-footer-privacy-link">
                Aviso de privacidad
              </a>
              <span className="text-xs text-muted-foreground">·</span>
              <a href="/terminos" className="text-xs text-[#2563EB] hover:underline" data-testid="landing-footer-terms-link">
                Términos de servicio
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Sticky Referral Bar */}
      {!stickyDismissed && (
        <div
          className="fixed bottom-0 left-0 right-0 z-50 bg-[#0F172A] border-t border-slate-700 shadow-2xl animate-slide-up"
          data-testid="landing-referral-sticky"
        >
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <Gift className="w-5 h-5 text-teal-400 flex-shrink-0" />
              <p className="text-sm text-slate-200 truncate md:whitespace-normal">
                <span className="font-medium text-white">¿Ya usas Klarvi?</span>{' '}
                <span className="hidden sm:inline">Invita a un amigo. Si se hace Pro, tú ganas: un mes gratis o descuento en tu mensualidad.</span>
                <span className="sm:hidden">Invita amigos y gana descuentos.</span>
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                size="sm"
                onClick={() => navigate(isAuthenticated ? '/dashboard' : '/login')}
                className="bg-teal-500 hover:bg-teal-600 text-white text-xs md:text-sm"
                data-testid="sticky-referral-cta"
              >
                {isAuthenticated ? 'Dashboard' : 'Inicia sesión'}
              </Button>
              <button
                onClick={() => setStickyDismissed(true)}
                className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
                data-testid="sticky-referral-dismiss"
                aria-label="Cerrar"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
